import { useEffect, useRef, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../theme/tokens';
import type { FindingDiff } from '../../types';
import { invalidateFindingSurfaces, useApplyProgress } from '../../api/hooks';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { DiffCard } from './DiffCard';

const t = darkTheme;

/**
 * SC-2580 — the chat card for a whole-finding apply the agent kicked off
 * ASYNCHRONOUSLY (BE `ApplyStartedOutput`, `outcome: 'started'`). The agent returns
 * immediately — its text message names the finding + how many entities it spans — and
 * this card shows the pre-apply `diff` under a status header.
 *
 * SC-3476 — the header follows the job to its end instead of saying "Applying…"
 * forever: it polls the same apply-progress read the table and drawer use (shared
 * cache, keyed on `finding_id`) and settles on "✓ Applied" or "Couldn't apply". A
 * replayed card whose job can't be found (`status: null`, or the read failed) says
 * the apply was started rather than pulsing on.
 */
export function StartedApplyingCard({
  findingId,
  diff,
  live = false,
}: {
  findingId: string;
  diff: FindingDiff | null;
  /** The card arrived in the live stream (not replayed history). Read once, at
   *  mount: the stream ends long before a big job does. */
  live?: boolean;
}) {
  const qc = useQueryClient();
  const { active } = useActiveCatalog();
  const catalogId = active?.id ?? '';
  const progress = useApplyProgress(catalogId, findingId);
  const status = progress.data?.status ?? null;

  // Refresh the finding surfaces when this card's job finishes — the queued apply
  // skipped that (so the finding isn't dropped mid-apply). A live card refreshes even
  // when its FIRST read is already `applied` (a small job can finish before the poll
  // starts); a card replayed from history whose job was done on its first read has
  // nothing to refresh. Once per card.
  const shouldRefresh = useRef(live);
  useEffect(() => {
    if (status === 'queued' || status === 'applying') shouldRefresh.current = true;
    else if (status === 'applied' && shouldRefresh.current) {
      shouldRefresh.current = false;
      invalidateFindingSurfaces(qc, catalogId);
    }
  }, [status, qc, catalogId]);

  const header =
    status === 'applied' ? (
      <span style={{ fontSize: 12.5, fontWeight: 600, color: t.accentText }}>✓ Applied</span>
    ) : status === 'failed' ? (
      <span style={{ fontSize: 12.5, fontWeight: 600, color: t.redText }}>Couldn&rsquo;t apply</span>
    ) : status === null && (progress.isError || progress.data != null) ? (
      <span style={{ fontSize: 12.5, fontWeight: 600, color: t.text2 }}>Apply started</span>
    ) : (
      <>
        <span aria-hidden style={dotStyle} />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: t.text }}>Applying…</span>
      </>
    );

  // No "N values updated" on `applied`: the job's `applied_cells` counts value ops
  // only (a find-and-replace or a split writes cells it doesn't count), and this card
  // has no ops to tell which kind of finding it is — the other surfaces quote the
  // count only behind `cellCountCoversAll`. The agent's own message names the change.
  const footer =
    status === 'applied'
      ? null
      : status === 'failed'
        ? (progress.data?.error ?? 'The apply failed. Ask EKOM to try again.')
        : status === null && (progress.isError || progress.data != null)
          ? 'Check the Findings view for the result.'
          : 'Running in the background — track progress in the table.';

  return (
    <div data-testid="started-applying-card" style={{ marginTop: 6 }}>
      <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {header}
      </div>
      {diff && <DiffCard diff={diff} />}
      {footer && <div style={{ fontSize: 12, color: t.text3, marginTop: 6 }}>{footer}</div>}
    </div>
  );
}

const dotStyle: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: t.accent,
  animation: 'ekomPulse 2s ease-in-out infinite',
};
